import React from 'react';
import styled from 'styled-components';
import { Copy, IdCard, CircleDot, Route, ArrowRight, AlertTriangle } from 'lucide-react';
import colors from 'theme/colors';
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent';
import { Card, Grid, Label, Muted, PillarBlock, Section } from '../parts';
import { FUNNEL_LABELS } from '../labels';

const FUNNEL_ORDER = ['discovery', 'consideration', 'conversation', 'conversion'];

export default function ProfileTab({ analysis, pillarsByTab, toast }) {
    const { result, previous } = analysis;
    const stages = FUNNEL_ORDER
        .map(key => result.funnel.stages.find(stage => stage.stage === key))
        .filter(Boolean);

    async function copyBio(text) {
        try {
            await navigator.clipboard.writeText(text);
            toast.success('Bio copiada! Cole no Instagram em Editar perfil.', { position: 'top-center' });
        } catch (error) {
            toast.error('Não foi possível copiar. Selecione o texto e copie manualmente.', { position: 'top-center' });
        }
    }

    return (
        <>
            {pillarsByTab.map(pillar => (
                <Section key={pillar.key} icon={IdCard} title={pillar.title}>
                    <PillarBlock pillar={pillar} previousScore={previous?.pillars?.[pillar.key]} />

                    {pillar.key === 'bio' && (
                        <Grid $min='280px'>
                            <Card>
                                <Label>Sua bio hoje</Label>
                                <BioText>{result.bio.current}</BioText>
                            </Card>
                            {result.bio.suggestions.map((suggestion, index) => (
                                <SuggestionCard key={index}>
                                    <SuggestionHead>
                                        <Label>{suggestion.label}</Label>
                                        <CopyButton type='button' onClick={() => copyBio(suggestion.text)}>
                                            <Copy size={13} /> Copiar
                                        </CopyButton>
                                    </SuggestionHead>
                                    <BioText>{suggestion.text}</BioText>
                                </SuggestionCard>
                            ))}
                        </Grid>
                    )}
                </Section>
            ))}

            <Section icon={CircleDot} title='Destaques' subtitle={result.highlights.verdict}>
                <Grid $min='280px'>
                    <Card>
                        <Label>Como estão</Label>
                        <Muted>{result.highlights.current_state}</Muted>
                    </Card>
                    <Card>
                        <Label>Como deveriam estar</Label>
                        <Muted>{result.highlights.ideal_state}</Muted>
                    </Card>
                </Grid>

                {result.highlights.ideal_sequence.length > 0 && (
                    <Card>
                        <Label>Ordem sugerida</Label>
                        <HighlightRow>
                            {result.highlights.ideal_sequence.map((highlight, index) => (
                                <Highlight key={index}>
                                    <HighlightCircle>{index + 1}</HighlightCircle>
                                    <HighlightName>{highlight.name}</HighlightName>
                                    <HighlightReason>{highlight.reason}</HighlightReason>
                                </Highlight>
                            ))}
                        </HighlightRow>
                    </Card>
                )}
            </Section>

            <Section icon={Route} title='Caminho até a compra' subtitle={result.funnel.verdict}>
                <Funnel>
                    {stages.map((stage, index) => (
                        <React.Fragment key={stage.stage}>
                            <Stage>
                                <StageName>{FUNNEL_LABELS[stage.stage]}</StageName>
                                <Muted>{stage.description}</Muted>
                                {stage.gap && (
                                    <Gap>
                                        <AlertTriangle size={13} />
                                        <span>{stage.gap}</span>
                                    </Gap>
                                )}
                            </Stage>
                            {index < stages.length - 1 && <StageArrow><ArrowRight size={16} /></StageArrow>}
                        </React.Fragment>
                    ))}
                </Funnel>
            </Section>
        </>
    );
}

const BioText = styled.p`
    margin: 0;
    white-space: pre-line;
    font-size: 14px;
    line-height: 1.6;
    color: ${colors.nightDark};
`;

const SuggestionCard = styled(Card)`
    border-color: ${panelAccentAlpha(colors.secondaryPurple, '55')};
`;

const SuggestionHead = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const CopyButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    border: none;
    background: transparent;
    color: ${panelAccent(colors.secondaryPurple)};
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    padding: 4px 6px;
    border-radius: 6px;

    &:hover {
        background: ${panelAccentAlpha(colors.secondaryPurple, '10')};
    }
`;

const HighlightRow = styled.div`
    display: flex;
    gap: 16px;
    overflow-x: auto;
    padding: 6px 2px 4px;
`;

const Highlight = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    width: 140px;
    flex-shrink: 0;
    text-align: center;
`;

const HighlightCircle = styled.span`
    width: 52px;
    height: 52px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    color: ${panelAccent(colors.secondaryPurple)};
    border: 2px solid ${panelAccentAlpha(colors.secondaryPurple, '66')};
    background: ${colors.neutralLight};
`;

const HighlightName = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const HighlightReason = styled.span`
    font-size: 12px;
    line-height: 1.4;
    color: ${colors.night};
`;

const Funnel = styled.div`
    display: flex;
    align-items: stretch;
    gap: 8px;

    @media screen and (max-width: 1000px) {
        flex-direction: column;
    }
`;

const Stage = styled(Card)`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const StageName = styled.span`
    font-size: 14px;
    font-weight: 600;
    color: ${panelAccent(colors.secondaryPurple)};
`;

const Gap = styled.div`
    display: flex;
    gap: 6px;
    align-items: flex-start;
    margin-top: auto;
    padding-top: 8px;
    border-top: 1px dashed ${colors.night}33;
    font-size: 12px;
    line-height: 1.45;
    color: ${colors.goldDark};

    svg {
        flex-shrink: 0;
        margin-top: 2px;
    }
`;

const StageArrow = styled.div`
    display: flex;
    align-items: center;
    color: ${colors.nightLight};

    @media screen and (max-width: 1000px) {
        justify-content: center;
        transform: rotate(90deg);
    }
`;
